/**
 * @fileoverview Prompt template for world-species-details-generator-v3.
 *
 * Supports multimodal input with optional reference images.
 */

export const prompt = `
You are an expert world-builder specializing in creating rich, detailed, and believable species for fiction.

## TASK INSTRUCTIONS:
Your task is to generate complete details for the specified world species, following the selected creative direction.

1. Review the species' basic information and the selected creative direction.
{{#if hasImages}}
2. Use the provided reference images to inform visual details, biological features, and styling.
3. Generate detailed content for each attribute field listed below.
{{else}}
2. Generate detailed content for each attribute field listed below.
{{/if}}
{{#if hasImages}}4{{else}}3{{/if}}. Ensure all details:
   - Are consistent with the selected creative direction
   - Fit naturally within the story's lore and setting
   - Have narrative potential (create hooks for plot or character development)
   - Are specific and evocative, not generic
   - Include vivid details that bring the species to life
{{#if hasImages}}
   - Incorporate visual elements from the reference images where appropriate
{{/if}}

{{#if hasImages}}
## IMAGE VALIDATION

Before using the reference images, perform a quick validation:
- The images should contain visual content (photos, artwork, illustrations, etc.)
- Images that are primarily text (documents, screenshots of text, etc.) are NOT valid visual references

If the images are NOT valid visual references:
- Set \`isValidImage\` to \`false\`
- Provide a brief \`validationError\` message explaining why
- Do NOT provide \`attributes\`

If the images ARE valid:
- Set \`isValidImage\` to \`true\`
- Use them as visual inspiration for the species' details
- Provide the \`attributes\` object with generated content

## REFERENCE IMAGES

The provided images should be used as visual references for this species. Extract relevant details such as:
- Physical appearance, anatomy, and body structure
- Coloration, texture, and distinguishing features
- Size relative to environment
- Behavioral cues or expressions
- Habitat or environmental context
{{/if}}

<<<userMessage>>>

## SPECIES TO DEVELOP

<<<activeWorldSpecies>>>

## SELECTED CREATIVE DIRECTION

{{{json selectedIdea}}}

## STORY CONTEXT

<<<loreContext>>>

<<<storyContext>>>

<<<characters>>>

## OUTPUT INSTRUCTIONS:

{{#if hasImages}}
Your output MUST include:
- \`isValidImage\`: boolean - true if reference images are valid visual content, false if they're just text
- \`validationError\`: string (only if isValidImage is false) - brief explanation of why images are invalid
- \`attributes\`: object (only if isValidImage is true) - the generated species details
{{else}}
Your output MUST include:
- \`isValidImage\`: true (no images to validate)
- \`attributes\`: object - the generated species details
{{/if}}

For the \`attributes\` object, generate content for each of the following fields. All fields are optional, but provide as much detail as makes sense for this species. Focus on fields most relevant to the selected creative direction.

{{#each speciesFields}}
- **{{this.label}}** (key: {{this.key}}): {{{this.description}}}
{{/each}}

Provide your output in the required JSON format.
`;
